import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Star, Trophy, MapPin, PawPrint, Clock, Trash2, Dog, Heart, MessageSquare } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import FeedbackDialog from "../components/FeedbackDialog";
import { placeUrl } from "../lib/placeUrl";
import { Avatar } from "../components/Avatar";
import { PhotoUploadButton } from "../components/PhotoUploadButton";

const INITIAL_HISTORY_ROWS = 10;

const Profile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  const load = () => api.get("/profile").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    if (!user) { navigate("/login?redirect=/profile"); return; }
    load();
  }, [user, navigate]);

  if (!data) return <div className="max-w-4xl mx-auto px-4 py-10 text-brand-ink/60">Loading profile…</div>;

  const initials = (data.user.name || "U").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const hasOwnPhoto = !!data.user.photo_url && data.user.photo_url !== data.user.picture;
  const setUserPhoto = (url) => setData((d) => ({ ...d, user: { ...d.user, photo_url: url } }));
  const setDogPhoto = (id, url) => setData((d) => ({ ...d, dogs: d.dogs.map((x) => (x.id === id ? { ...x, photo_url: url } : x)) }));

  const removeDog = async (id) => {
    if (!confirm("Remove this dog from your profile?")) return;
    try {
      await api.delete(`/dogs/${id}`);
      toast.success("Dog removed");
      load();
    } catch { toast.error("Could not remove"); }
  };

  return (
    <div className="bg-brand-cream min-h-[calc(100vh-4rem)] pb-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8" data-testid="profile-page">
        {/* Account header. Centered, generously-spaced stack:
            avatar → name → email → 2x2 stats grid → feedback button.
            Replaces the prior cramped horizontal layout where the
            avatar, name, stats chips, and feedback button all
            competed for one row's worth of width. Specs locked by
            the user; sizes use raw px values (not Tailwind size scale)
            for pixel-perfect compliance with the design brief. */}
        <div
          className="bg-white rounded-3xl border border-brand-border-warm shadow-warm px-6 sm:px-10 py-10 sm:py-12"
          data-testid="profile-header"
        >
          <div className="max-w-md mx-auto flex flex-col items-center">
            {/* Avatar — 72px photo, or primary-green initials circle */}
            <Avatar src={data.user.photo_url} name={data.user.name} initials={initials} size={72} fontSize={26} testId="profile-avatar" />
            <div className="mt-3">
              <PhotoUploadButton endpoint="/profile/photo" hasPhoto={hasOwnPhoto} onChange={setUserPhoto} testIdPrefix="profile" />
            </div>

            {/* Name — 18px / weight 500, centered */}
            <h1
              data-testid="profile-name"
              className="font-heading text-brand-ink text-center mt-5"
              style={{ fontSize: 18, fontWeight: 500, lineHeight: 1.2 }}
            >
              {data.user.name}
            </h1>

            {/* Email — 13px muted, centered */}
            <div
              data-testid="profile-email"
              className="text-center mt-1.5 break-all"
              style={{ fontSize: 13, color: "#6b6560", lineHeight: 1.4 }}
            >
              {data.user.email}
            </div>

            {/* Stats — 2x2 grid, 8px gap. Each card: cream bg,
                rounded corners, centered. Number 20px/weight 500,
                label 12px muted. Card padding tuned so a 4-digit
                number doesn't get cramped on mobile. */}
            <div
              data-testid="profile-stats-grid"
              className="grid grid-cols-2 w-full mt-8"
              style={{ gap: 8 }}
            >
              {[
                { key: "checkins",     label: "Check-ins",    value: data.stats.checkins },
                { key: "unique-parks", label: "Unique parks", value: data.stats.unique_parks },
                { key: "reviews",      label: "Reviews",      value: data.stats.reviews },
                { key: "saved",        label: "Saved",        value: data.stats.saved ?? 0 },
              ].map((s) => (
                <div
                  key={s.key}
                  data-testid={`profile-stat-${s.key}`}
                  className="bg-[#FAF7F2] rounded-xl flex flex-col items-center justify-center"
                  style={{ padding: "14px 12px" }}
                >
                  <div
                    className="text-brand-ink"
                    style={{ fontSize: 20, fontWeight: 500, lineHeight: 1.1 }}
                  >
                    {s.value}
                  </div>
                  <div
                    className="mt-1"
                    style={{ fontSize: 12, color: "#6b6560" }}
                  >
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            {/* New-user nudge — only when the account has 0 check-ins.
                Reframes the all-zero stat row from a "scoreboard" into
                an inviting next step. Hidden the moment the user has
                their first check-in (or as soon as they log one). */}
            {data.stats.checkins === 0 && (
              <div
                data-testid="profile-empty-state-nudge"
                className="w-full mt-8 rounded-2xl flex flex-col items-center text-center"
                style={{
                  backgroundColor: "#E8F2EC",
                  padding: "28px 24px",
                }}
              >
                <div
                  className="font-heading text-brand-ink"
                  style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.3 }}
                >
                  Your pack&apos;s just getting started 🐾
                </div>
                <p
                  className="mt-2 max-w-sm"
                  style={{ fontSize: 14, color: "#6b6560", lineHeight: 1.5 }}
                >
                  Check in at your first park and your activity will show up here.
                </p>
                <Link
                  to="/search"
                  data-testid="profile-find-a-park-cta"
                  className="inline-flex items-center justify-center mt-5 rounded-full font-semibold hover:bg-brand-forest-dark active:scale-95 transition-all"
                  style={{
                    backgroundColor: "#4A7C59",
                    color: "#FFFFFF",
                    fontSize: 14,
                    padding: "11px 22px",
                  }}
                >
                  Find a park
                </Link>
              </div>
            )}

            {/* Feedback — full-width outline button at the bottom.
                Border #e5e0d8 (matches the spec — slightly cooler
                than brand-border-warm), no fill, 13px text + icon. */}
            <button
              data-testid="open-feedback-btn"
              onClick={() => setFeedbackOpen(true)}
              className="w-full mt-8 rounded-full inline-flex items-center justify-center gap-2 text-brand-ink hover:text-brand-forest hover:border-brand-forest active:scale-[0.99] transition-colors"
              style={{
                fontSize: 13,
                paddingTop: 11,
                paddingBottom: 11,
                border: "1px solid #e5e0d8",
                backgroundColor: "transparent",
              }}
            >
              <MessageSquare className="w-4 h-4" />
              Send feedback
            </button>
          </div>
        </div>

        {/* Parks Explored + Visit History.
            Two card sections injected between the stats grid and the
            main two-column activity layout. Both are hidden entirely
            when the user has zero check-ins — the existing empty-state
            nudge above already invites them to check in. */}
        {data.stats.checkins > 0 && (() => {
          const explored = data.stats.unique_parks || 0;
          const totalDogParks = data.stats.total_dog_parks || 0;
          // Clamp to [0, 100] — guards against the (impossible-but-cheap)
          // edge case of explored > totalDogParks while the parks cache
          // is mid-rebuild.
          const pct = totalDogParks > 0
            ? Math.min(100, Math.max(0, (explored / totalDogParks) * 100))
            : 0;
          const history = data.visit_history || [];
          const visible = historyExpanded ? history : history.slice(0, INITIAL_HISTORY_ROWS);
          const hiddenCount = history.length - visible.length;

          return (
            <div className="space-y-6 mt-8">
              {/* ---- Parks Explored ---- */}
              <section
                data-testid="profile-parks-explored"
                className="bg-white border border-brand-border-warm rounded-xl"
                style={{ padding: "1.5rem" }}
              >
                <h2
                  className="font-heading text-brand-ink"
                  style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, margin: 0 }}
                >
                  Parks Explored
                </h2>
                <div
                  className="mt-3 flex items-baseline gap-2 font-body"
                  data-testid="profile-explored-count"
                >
                  <span style={{ fontSize: 18 }} aria-hidden="true">🐾</span>
                  <span
                    className="text-brand-ink"
                    style={{ fontSize: 18, fontWeight: 600 }}
                  >
                    {explored} park{explored === 1 ? "" : "s"} explored
                  </span>
                </div>
                <div
                  role="progressbar"
                  aria-valuenow={Math.round(pct)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`${explored} of ${totalDogParks} parks explored`}
                  data-testid="profile-explored-bar"
                  className="w-full mt-3 overflow-hidden"
                  style={{ height: 8, backgroundColor: "#E8F2EC", borderRadius: 999 }}
                >
                  <div
                    className="h-full"
                    style={{
                      width: `${pct}%`,
                      backgroundColor: "#4A7C59",
                      borderRadius: 999,
                      transition: "width 600ms ease-out",
                    }}
                  />
                </div>
                <p
                  className="font-body mt-2"
                  style={{ fontSize: 13, color: "#8a857d" }}
                >
                  {explored.toLocaleString()} of {totalDogParks.toLocaleString()} parks explored
                </p>
                <Link
                  to="/search"
                  data-testid="profile-discover-park-link"
                  className="inline-block mt-3 font-body font-semibold hover:underline"
                  style={{ color: "#4A7C59", fontSize: 14 }}
                >
                  Discover a new park →
                </Link>
              </section>

              {/* ---- Visit History timeline ---- */}
              <section
                data-testid="profile-visit-history"
                className="bg-white border border-brand-border-warm rounded-xl"
                style={{ padding: "1.5rem" }}
              >
                <h2
                  className="font-heading text-brand-ink"
                  style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, margin: 0 }}
                >
                  Visit History
                </h2>
                <ol
                  className="mt-4 divide-y divide-brand-border-warm"
                  data-testid="profile-visit-history-list"
                >
                  {visible.map((v, idx) => (
                    <li
                      key={`${v.park_id}-${v.at}-${idx}`}
                      data-testid={`profile-visit-row-${idx}`}
                      className="py-3 flex items-baseline justify-between gap-3 font-body"
                    >
                      <div className="min-w-0 flex-1">
                        <Link
                          to={`/park/${v.park_id}`}
                          className="text-brand-ink hover:text-brand-forest hover:underline truncate block"
                          style={{ fontSize: 15, fontWeight: 500 }}
                        >
                          {v.park_name}
                        </Link>
                        {/* Visit rows carry only `park_id` from the
                            /profile/visits payload — no slug/city/state
                            to build a canonical URL client-side. The
                            legacy /park/{id} route still resolves and
                            the backend redirects to the canonical URL
                            server-side (via db.place_redirects) on hit. */}
                        {v.dog_name && (
                          <div
                            className="mt-0.5 truncate flex items-center gap-1.5"
                            style={{ fontSize: 13, color: "#6b6560" }}
                          >
                            {v.dog_photo_url && <Avatar src={v.dog_photo_url} name={v.dog_name} size={18} testId={`profile-visit-dog-photo-${idx}`} />}
                            <span>with <span className="font-semibold">{v.dog_name}</span></span>
                          </div>
                        )}
                        {/* Dog Journal note — optional free-text the
                            user added on the post-check-in bottom
                            sheet. Italic + muted so it reads as a
                            footnote to the main visit line, not a
                            second-class entry. Hidden entirely when
                            absent (no empty state). */}
                        {v.note && (
                          <div
                            data-testid={`profile-visit-note-${idx}`}
                            className="mt-1 italic"
                            style={{
                              fontSize: 12,
                              color: "#7A7A60",
                              lineHeight: 1.4,
                              whiteSpace: "pre-line",
                            }}
                          >
                            {v.note}
                          </div>
                        )}
                      </div>
                      <time
                        className="whitespace-nowrap shrink-0"
                        style={{ fontSize: 13, color: "#8a857d" }}
                        dateTime={v.at}
                      >
                        {format(new Date(v.at), "EEE, MMM d")}
                      </time>
                    </li>
                  ))}
                </ol>
                {hiddenCount > 0 && (
                  <button
                    type="button"
                    data-testid="profile-history-show-more"
                    onClick={() => setHistoryExpanded(true)}
                    className="mt-4 font-body font-semibold hover:underline"
                    style={{ color: "#4A7C59", fontSize: 14, background: "none", border: "none", padding: 0, cursor: "pointer" }}
                  >
                    Show {hiddenCount} more visit{hiddenCount === 1 ? "" : "s"}
                  </button>
                )}
              </section>
            </div>
          );
        })()}

        <div className="grid lg:grid-cols-12 gap-8 mt-8">
          {/* Left: dogs + activity */}
          <div className="lg:col-span-7 space-y-8">
            <section>
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="font-heading text-2xl font-bold text-brand-ink">Your dogs</h2>
                <Link
                  to="/onboarding"
                  data-testid="add-dog-btn"
                  className="inline-flex items-center gap-1 text-sm font-semibold text-brand-forest hover:text-brand-forest-dark"
                >
                  <Plus className="w-4 h-4" /> Add dog
                </Link>
              </div>
              {data.dogs.length === 0 ? (
                /* Skipped-onboarding nudge — light green card matching
                   the existing empty-state CTA aesthetic. Spec asks for
                   centered text + primary-green button so the user has
                   a single obvious next step to unlock check-ins. */
                <div
                  data-testid="profile-add-dog-nudge"
                  className="rounded-2xl p-6 text-center"
                  style={{ backgroundColor: "#E8F2EC", border: "1px solid #C5DCC9" }}
                >
                  <div
                    className="font-body"
                    style={{ fontSize: 15, color: "#1A1A0F", lineHeight: 1.45 }}
                  >
                    Add your dog to get the full Pawground experience 🐾
                  </div>
                  <Link
                    to="/onboarding"
                    data-testid="profile-add-dog-cta"
                    className="inline-block mt-4 font-body font-semibold rounded-full"
                    style={{ backgroundColor: "#4A7C59", color: "#FFFFFF", padding: "10px 20px", fontSize: 14 }}
                  >
                    Add Your Dog
                  </Link>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4" data-testid="dogs-grid">
                  {data.dogs.map((d) => (
                    <div key={d.id} className="bg-white rounded-2xl p-5 border border-brand-border-warm shadow-warm">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar src={d.photo_url} name={d.name} initials={d.name.slice(0, 1).toUpperCase()} size={48} fontSize={18} testId={`dog-avatar-${d.id}`} />
                          <div className="min-w-0">
                            <div className="font-heading text-lg font-semibold text-brand-ink truncate">{d.name}</div>
                            <div className="text-xs text-brand-ink/60 truncate">
                              {d.breed} · {d.size || "—"} · {d.gender || "—"}
                              {d.age ? ` · ${d.age}y` : ""}
                            </div>
                            <div className="mt-1.5">
                              <PhotoUploadButton endpoint={`/dogs/${d.id}/photo`} hasPhoto={!!d.photo_url} onChange={(url) => setDogPhoto(d.id, url)} testIdPrefix={`dog-${d.id}`} />
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => removeDog(d.id)}
                          data-testid={`remove-dog-${d.id}`}
                          className="text-brand-ink/40 hover:text-red-500 transition-colors"
                          aria-label="Remove dog"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {d.personality?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {d.personality.map((t) => (
                            <span key={t} className="text-[11px] font-semibold uppercase tracking-wider text-brand-forest bg-brand-light-green px-2 py-0.5 rounded-full">
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-brand-ink mb-3">Recent activity</h2>
              {data.activity.length === 0 ? (
                <div className="bg-white rounded-2xl p-6 border border-brand-border-warm text-sm text-brand-ink/60 text-center">
                  Your check-ins and reviews will appear here.
                </div>
              ) : (
                <ol className="border-l-2 border-brand-border-warm pl-4 space-y-4" data-testid="activity-feed">
                  {data.activity.map((a, i) => (
                    <li key={i} className="relative">
                      <span className="absolute -left-[22px] top-1 w-3 h-3 rounded-full bg-brand-forest border-2 border-brand-cream" />
                      <div className="bg-white rounded-2xl p-4 border border-brand-border-warm">
                        <div className="flex items-center gap-2 text-xs text-brand-ink/60">
                          {a.type === "checkin" ? <PawPrint className="w-3.5 h-3.5 text-brand-forest" /> : <Star className="w-3.5 h-3.5 text-amber-500" />}
                          <span>{formatDistanceToNow(new Date(a.at))} ago</span>
                        </div>
                        <div className="text-sm text-brand-ink mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1">
                          {a.type === "checkin" ? (
                            <span className="inline-flex items-center gap-1.5 flex-wrap">Checked in at <span className="font-semibold">{a.park_name}</span>{a.dog_name ? <> with {a.dog_photo_url && <Avatar src={a.dog_photo_url} name={a.dog_name} size={18} testId={`activity-dog-photo-${i}`} />}<span className="font-semibold">{a.dog_name}</span></> : null}</span>
                          ) : (
                            <>
                              <span>Reviewed <span className="font-semibold">{a.park_name}</span> ·</span>
                              <span
                                className="inline-flex items-center gap-0.5"
                                aria-label={`${a.rating} out of 5 stars`}
                                data-testid={`activity-review-stars-${i}`}
                              >
                                {[1, 2, 3, 4, 5].map((n) => (
                                  <Star
                                    key={n}
                                    className={`w-3.5 h-3.5 ${n <= a.rating ? "fill-amber-400 text-amber-400" : "fill-brand-border-warm text-brand-border-warm"}`}
                                  />
                                ))}
                              </span>
                            </>
                          )}
                        </div>
                        {a.type === "review" && a.text && (
                          <p className="text-sm text-brand-ink/70 mt-1">{a.text}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </div>

          {/* Right: favorites + badges */}
          <div className="lg:col-span-5 space-y-8">
            <section>
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="font-heading text-2xl font-bold text-brand-ink flex items-center gap-2">
                  <Heart className="w-5 h-5 text-rose-500 fill-rose-500" />
                  Saved parks
                </h2>
                <span className="text-xs text-brand-ink/60">{data.saved_parks?.length || 0}</span>
              </div>
              {(!data.saved_parks || data.saved_parks.length === 0) ? (
                <div className="bg-white rounded-2xl p-6 border border-brand-border-warm text-sm text-brand-ink/60 text-center">
                  Bookmark parks from their detail page to find them here.
                </div>
              ) : (
                <div className="space-y-3" data-testid="saved-parks">
                  {data.saved_parks.map(({ park, saved_at }) => (
                    <Link
                      key={park.id}
                      to={placeUrl(park)}
                      className="block bg-white rounded-2xl p-4 border border-brand-border-warm hover:border-brand-forest transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-heading font-semibold text-brand-ink truncate">{park.name}</div>
                          <div className="text-xs text-brand-ink/60 flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3 h-3" /> {park.city}, {park.state}
                          </div>
                        </div>
                        <span className="text-[11px] font-semibold bg-rose-50 text-rose-500 px-2 py-1 rounded-full flex items-center gap-1 whitespace-nowrap">
                          <Heart className="w-3 h-3 fill-rose-500" />
                          Saved
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            <section>
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="font-heading text-2xl font-bold text-brand-ink flex items-center gap-2">
                  <PawPrint className="w-5 h-5 text-brand-forest" />
                  Parks visited
                </h2>
                <span className="text-xs text-brand-ink/60">{data.visited_parks?.length || 0}</span>
              </div>
              {(!data.visited_parks || data.visited_parks.length === 0) ? (
                <div className="bg-white rounded-2xl p-6 border border-brand-border-warm text-sm text-brand-ink/60 text-center">
                  Check in at your first park to build your list.
                </div>
              ) : (
                <div className="space-y-3" data-testid="visited-parks">
                  {data.visited_parks.map(({ park, visits }) => (
                    <Link
                      key={park.id}
                      to={placeUrl(park)}
                      className="block bg-white rounded-2xl p-4 border border-brand-border-warm hover:border-brand-forest transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-heading font-semibold text-brand-ink truncate">{park.name}</div>
                          <div className="text-xs text-brand-ink/60 flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3 h-3" /> {park.city}, {park.state}
                          </div>
                        </div>
                        <span className="text-xs font-bold bg-brand-light-green text-brand-forest px-2 py-1 rounded-full whitespace-nowrap">
                          {visits} visit{visits === 1 ? "" : "s"}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-brand-ink mb-3">Badges</h2>
              {data.badges.length === 0 ? (
                <div className="bg-white rounded-2xl p-6 border border-brand-border-warm text-sm text-brand-ink/60 text-center">
                  Earn badges by checking in and reviewing parks.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3" data-testid="badges-grid">
                  {data.badges.map((b) => (
                    <div key={b.id} className="bg-white rounded-2xl p-4 border border-brand-border-warm text-center">
                      <span className="w-12 h-12 rounded-full bg-brand-light-green text-brand-forest flex items-center justify-center mx-auto mb-2">
                        <Trophy className="w-6 h-6" />
                      </span>
                      <div className="font-heading font-semibold text-brand-ink text-sm">{b.name}</div>
                      <div className="text-xs text-brand-ink/60 mt-1">{b.description}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
      <FeedbackDialog
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        defaultEmail={data.user.email}
      />
    </div>
  );
};

export default Profile;
